import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { UserCircle } from "@phosphor-icons/react";

/**
 * Forces users who signed up before the expanded registration form (or whose
 * profile is missing Phone / Employer / Position) to complete it once on login.
 * Super admins, admins and instructors are exempt — only students are gated.
 */
export default function ProfileCompletionGate() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({
    phone: "",
    school_name: "",
    position: "",
    interest_type: "",
  });
  const [saving, setSaving] = useState(false);

  // Only gate students/alumni; skip for admins & instructors.
  // The user said: "Remove the secondary profile completion prompt where the
  // required information has already been collected through the account
  // creation form, but if the account was created by an admin, the student
  // should be prompted to fill the information."
  // → Gate ONLY when (a) profile is explicitly incomplete AND (b) user was
  //   admin-created OR is genuinely missing the core fields.
  const gatedRoles = ["student", "alumni"];
  const hasCoreInfo = !!(user?.phone && user?.school_name && user?.position);
  const shouldGate =
    user &&
    gatedRoles.includes(user.role) &&
    user.profile_complete !== true &&
    user.admin_created === true &&
    !hasCoreInfo;

  useEffect(() => {
    if (user) {
      setForm({
        phone: user.phone || "",
        school_name: user.school_name || "",
        position: user.position || "",
        interest_type: user.interest_type || "",
      });
    }
  }, [user]);

  const handleSubmit = async () => {
    if (!form.phone || !form.school_name || !form.position) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/profile", form);
      toast.success("Profile updated — thank you!");
      await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update profile");
    }
    setSaving(false);
  };

  if (!shouldGate) return null;

  return (
    <Dialog open={true}>
      <DialogContent
        className="rounded-none sm:max-w-lg max-h-[92vh] overflow-y-auto"
        // Block dismissing - user MUST complete profile
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="profile-completion-gate"
      >
        <DialogHeader>
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
            <UserCircle size={28} className="text-blue-800" weight="fill" />
          </div>
          <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>
            Complete Your Profile
          </DialogTitle>
          <DialogDescription>
            Welcome back, {user?.name?.split(" ")[0] || "there"}! We've updated our platform
            and need a few extra details from you to personalise your learning experience.
            This will only take a minute.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-sm">Phone Number <span className="text-red-500">*</span></Label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="mt-1 rounded-none"
              placeholder="+234 ..."
              data-testid="gate-phone"
            />
          </div>

          <div>
            <Label className="text-sm">Current Employer / School Name <span className="text-red-500">*</span></Label>
            <Input
              value={form.school_name}
              onChange={(e) => setForm({ ...form, school_name: e.target.value })}
              className="mt-1 rounded-none"
              placeholder="e.g. Greenfield Academy"
              data-testid="gate-employer"
            />
          </div>

          <div>
            <Label className="text-sm">Your Role / Position at Work <span className="text-red-500">*</span></Label>
            <Input
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
              className="mt-1 rounded-none"
              placeholder="e.g. Principal, Head of School, Teacher"
              data-testid="gate-position"
            />
          </div>

          <div>
            <Label className="text-sm">Primary Interest</Label>
            <Select
              value={form.interest_type || "none"}
              onValueChange={(v) => setForm({ ...form, interest_type: v === "none" ? "" : v })}
            >
              <SelectTrigger className="mt-1 rounded-none" data-testid="gate-interest">
                <SelectValue placeholder="Optional" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Prefer not to say</SelectItem>
                <SelectItem value="programme">Full Programme</SelectItem>
                <SelectItem value="paid_course">Paid Course</SelectItem>
                <SelectItem value="free_course">Free Course</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none mt-2"
            data-testid="gate-submit-btn"
          >
            {saving ? "Saving..." : "Save & Continue"}
          </Button>
          <p className="text-xs text-slate-400 text-center">Required fields are marked with *</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
